import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { ArrowLeft, Package, Tag, User, Star, Download } from 'lucide-react';
import { API } from '../config';


export default function ModuleDetailPage() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const [mod, setMod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/marketplace/${slug}`)
      .then(r => setMod(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen bg-background" data-testid="module-detail-page">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link to="/marketplace" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-white text-sm mb-8 transition-colors" data-testid="module-back-link">
            <ArrowLeft className="w-4 h-4" />{lang === 'fr' ? 'Retour au marketplace' : 'Back to marketplace'}
          </Link>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : notFound ? (
            <div className="text-center py-20">
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">404</h2>
              <p className="text-muted-foreground">{lang === 'fr' ? 'Module introuvable.' : 'Module not found.'}</p>
            </div>
          ) : mod && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="module-detail-content">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                  <Package className="w-7 h-7 text-blue-400" />
                </div>
                <div>
                  <h1 className="font-heading text-3xl font-bold text-foreground" data-testid="module-detail-title">{mod.name}</h1>
                  <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{mod.creator_name}</span>
                    <span>v{mod.version}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-8">
                <span className={`text-lg font-bold ${mod.price === 0 ? 'text-emerald-400' : 'text-white'}`}>
                  {mod.price === 0 ? (lang === 'fr' ? 'Gratuit' : 'Free') : `${mod.price} ${mod.currency?.toUpperCase()}`}
                </span>
                <span className="px-3 py-1 rounded-full bg-secondary text-muted-foreground text-xs">{mod.category}</span>
              </div>

              {mod.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {mod.tags.map((tag, i) => (
                    <span key={i} className="flex items-center gap-1 px-3 py-1 rounded-full bg-secondary/60 border border-border/50 text-xs text-muted-foreground">
                      <Tag className="w-3 h-3" />{tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="p-6 rounded-2xl bg-card border border-border">
                <h2 className="font-heading text-lg font-medium text-foreground mb-3">Description</h2>
                <div className="space-y-4">
                  {mod.description.split('\n\n').map((p, i) => (
                    <p key={i} className="text-foreground/80 text-sm leading-relaxed">{p}</p>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
